import React, { useState, useEffect } from "react";

import { Trans, useTranslation } from "react-i18next";

import { getMappedFilters, getSessionFilters } from "../../utils/FilterHelpers";
import { ROLES } from "../../utils/constants";
import { getRoles } from "../../utils/OktaHelpers";
import { isAuthorized } from "../../utils/RolesHelpers";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import { getCurrentDateYMD } from "../../utils/DateHelper";
import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import Pagination from "../../components/Pagination/Pagination";
import CardTable from "../../components/Tables/CardTable";
import ScannersAPI from "../../API/scanners.api";
import DevicesModal from "../../components/Modals/DevicesModal";
import { longDateTime } from "../../utils/DateHelper";
import LookupsAPI from "../../API/lookups.api";
import { SuccessAlert, ErrorAlert } from "../../utils/Alerts";
import { validate } from "../../utils/ValidationHelpers";
import DevicesSchema from "../../schemas/devices.schema";
import ManageFirmwareModal from "../../components/Modals/ManageFirmwareModal";
import { setObject, getObject } from "../../utils/StorageHelpers";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { mapLookupToSelect } from "../../utils/Helpers";
import MissingData from "../../components/Shared/MissingData";

const DeviceManagement = () => {
    //Data States
    const [devices, setDevices] = useState([]);

    //General States
    const [isLoading, setIsLoading] = useState(false);

    //Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [devicesTotalCount, setDevicesTotalCount] = useState(0);

    //Filter States
    const [filters, setFilters] = useState({});
    const [filtersUsed, setFiltersUsed] = useState(false);
    const [organizationIds, setOrganizationIds] = useState([]);
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getSessionFilters("devices-filters")
    );
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    //Devices Form Validation States
    const [devicesError, setDevicesError] = useState({});
    const [isDevicesFormValid, setIsDevicesFormValid] = useState(false);
    const [isDevicesFromSubmitted, setDevicesIsFromSubmitted] = useState(false);

    //Devices Modal States
    const [showDevicesModal, setShowDevicesModal] = useState(false);
    const [isDevicesModalSubmitting, setIsDevicesModalSubmitting] =
        useState(false);
    const [devicesModalData, setDevicesModalData] = useState({});

    //Firmware Form Validation States
    const [firmwareError, setFirmwareError] = useState({});
    const [isFirmwareFormValid, setIsFirmwareFormValid] = useState(false);
    const [isFirmwareFromSubmitted, setFirmwareIsFromSubmitted] =
        useState(false);

    //Manage Firmware Modal States
    const [showFirmwareModal, setShowFirmwareModal] = useState(false);
    const [isFirmwareModalSubmitting, setIsFirmwareModalSubmitting] =
        useState(false);
    const [firmwareModalData, setFirmwareModalData] = useState({});

    //Localization
    const { t } = useTranslation();

    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Device-Management-itemsPerPage", true) || 10
    );

    const isSupport = () => {
        return isAuthorized([ROLES.support], getRoles());
    };

    const keys = [
        { name: "name" },
        { name: "currentFirmwareVersion" },
        {
            name: "latestFirmwareVersion",
            role: !isAuthorized([ROLES.support], getRoles()),
        },
        {
            name: "isLatest",
            element: (row) =>
                row ? (
                    <div className="flex items-center justify-center">
                        <div
                            className={`tag-ele tag-${
                                row.isLatest ? "completed" : "bundle"
                            } px-2`}
                        >
                            {t(`${row.isLatest ? "latest" : "outdated"}`)}
                        </div>
                    </div>
                ) : (
                    "---"
                ),
        },
        { name: "organization" },
        { name: "createdAt", parseMethod: longDateTime },
    ];

    const headers = [
        { label: t("deviceId"), name: "name" },
        {
            label: t("currentFirmwareVersion"),
            name: "currentFirmwareVersion",
            skipSort: true,
        },
        {
            label: t("latestFirmwareVersion"),
            name: "latestFirmwareVersion",
            role: !isAuthorized([ROLES.support], getRoles()),
        },
        { label: t("isLatest"), name: "latest", skipSort: true },
        { label: t("affiliate"), name: "organization" },
        { label: t("createdAt"), name: "createdAt" },
        {
            label: t("actions"),
            skipSort: true,
            role: !isSupport(),
        },
    ];

    const searchFilters = [
        {
            label: t("deviceId"),
            name: "name",
            type: "Input",
            placeholder: t("inputPlaceholder", { entity: t("deviceName") }),
        },
        {
            label: t("affiliate"),
            name: "organizationId",
            type: "Dropdown",
            options: organizationIds,
        },
        {
            label: t("createdAt"),
            name: "createdAt",
            type: "Date",
            max: getCurrentDateYMD(),
        },
    ];

    useEffect(() => {
        fetchOrganizations();
    }, []);

    useEffect(() => {
        validate(
            devicesModalData,
            DevicesSchema.DeivceRequestSchema,
            setIsDevicesFormValid,
            setDevicesError
        );
    }, [devicesModalData]);

    useEffect(() => {
        validate(
            firmwareModalData,
            DevicesSchema.FirmwareSchema,
            setIsFirmwareFormValid,
            setFirmwareError
        );
    }, [firmwareModalData]);

    useEffect(() => {
        if (sortBy && sortDirection) {
            setCurrentPage(1);
            listDevices(appliedFilters, sortBy, sortDirection);
        } else {
            setCurrentPage(1);
            listDevices(appliedFilters);
        }
    }, [sortBy, sortDirection]);

    const listDevices = async (
        filters,
        defaultSortBy,
        defaultSortDirection,
        offset,
        itemsPerPage = itemsPerRequest || 10
    ) => {
        setIsLoading(true);
        const result = await ScannersAPI.listScanners(
            itemsPerPage,
            getMappedFilters(filters),
            offset,
            defaultSortBy,
            defaultSortDirection
        );

        setDevicesTotalCount(result?.totalCount || 0);
        setDevices(result?.data || []);
        setIsLoading(false);
    };

    const handleDevicesModalSuccess = (closeModal) => {
        if (closeModal) setShowDevicesModal(false);
        setDevicesIsFromSubmitted(false);
        listDevices(filters);
    };

    const handleFirmwareModalSuccess = (closeModal) => {
        if (closeModal) setShowFirmwareModal(false);
        setFirmwareIsFromSubmitted(false);
        listDevices(filters);
    };

    const createDevice = async () => {
        setDevicesIsFromSubmitted(true);
        if (!isDevicesFormValid) return;

        setIsDevicesModalSubmitting(true);
        const result = await ScannersAPI.createScanner(devicesModalData);
        if (result?.inserted) {
            SuccessAlert(t("createDeviceSuccessMessage"));
            LogCapture.info(
                `Device created with name: ${devicesModalData.name}`
            );
            handleDevicesModalSuccess(true);
        } else {
            ErrorAlert(t("createDeviceFailureMessage"));
            LogCapture.error(
                `Couldn't create device with name: ${devicesModalData.name}`
            );
        }
        setIsDevicesModalSubmitting(false);
    };

    const updateDevice = async () => {
        setDevicesIsFromSubmitted(true);
        if (!isDevicesFormValid) return;
        setIsDevicesModalSubmitting(true);
        const result = await ScannersAPI.updateScanner(devicesModalData);
        if (result?.modified) {
            SuccessAlert(t("updateDeviceSuccessMessage"));
            LogCapture.info(
                `Device updated with name: ${devicesModalData.name}`
            );
            handleDevicesModalSuccess(true);
        } else {
            LogCapture.error(
                `Couldn't update device with name: ${devicesModalData.name}`
            );
        }
        setIsDevicesModalSubmitting(false);
    };

    const mapFirmwareData = () => {
        return firmwareModalData.devices.map((device) => ({
            id: device.value,
            firmwareVersion: firmwareModalData.firmwareVersion,
        }));
    };

    const submitFirmware = async () => {
        setFirmwareIsFromSubmitted(true);

        if (!isFirmwareFormValid) return;
        setIsFirmwareModalSubmitting(true);

        const result = await ScannersAPI.updateBulkScanners(mapFirmwareData());
        if (result.modified) {
            SuccessAlert(t("updateDeviceSuccessMessage"));
            LogCapture.info(
                `${firmwareModalData?.devices?.length} devices updated`
            );
            handleFirmwareModalSuccess(true);
        } else {
            ErrorAlert(t("updateDeviceFailureMessage"));
            LogCapture.error(
                `Couldn't update ${firmwareModalData?.devices?.length} devices`
            );
        }

        setIsFirmwareModalSubmitting(false);
    };

    const fetchOrganizations = async () => {
        let result = await LookupsAPI.getOrganizations();
        if (result) {
            setOrganizationIds(mapLookupToSelect(result));
        }
    };

    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            listDevices(filters, sortBy, sortDirection, index - 1);
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Device-Management-itemsPerPage", itemsPerPage, true);
        // call API to update the items per page
        listDevices(filters, sortBy, sortDirection, 0, itemsPerPage);
    };

    const applyFilters = (specificAppliedFilters) => {
        listDevices(specificAppliedFilters, sortBy, sortDirection);
        setFiltersUsed(true);
    };

    const handleEditClicked = (index) => {
        const selectedDevice = devices[index];
        setDevicesModalData({
            id: selectedDevice.id,
            name: selectedDevice.name,
            firmwareVersion: selectedDevice.latestFirmwareVersion,
        });
        setShowDevicesModal(true);
    };

    return (
        <>
            <header className="main-content-header flex items-center justify-between">
                <h1 className="h1">
                    <Trans i18nKey="deviceManagement">Device Management</Trans>
                </h1>
                <div className="action-header-wrapper flex items-center">
                    <FilterDropdown
                        filterOptions={searchFilters}
                        filters={filters}
                        setFilters={setFilters}
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                        component="devices"
                        applyFilters={applyFilters}
                    />
                    {isSupport() && (
                        <>
                            <button
                                className="default-orange-outline-btn ml-2"
                                onClick={() => {
                                    setShowDevicesModal(true);
                                }}
                            >
                                <i className="fas fa-plus mr-2" />
                                <Trans i18nKey="newDevice">New Device</Trans>
                            </button>
                            <button
                                className="default-orange-outline-btn ml-2"
                                onClick={() => {
                                    setShowFirmwareModal(true);
                                }}
                            >
                                <i className="fas fa-cog mr-2" />
                                <Trans i18nKey="manageFirmware">
                                    Manage Firmware
                                </Trans>
                            </button>
                        </>
                    )}
                </div>
            </header>
            <LoadingOverlay active={isLoading}>
                <div className="card-table shadow">
                    {devicesTotalCount > 0 ? (
                        <>
                            <CardTable
                                keys={keys}
                                headers={headers}
                                rows={devices}
                                handleEditClicked={handleEditClicked}
                                purpose={"devices"}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                sortDirection={sortDirection}
                                setSortDirection={setSortDirection}
                                actionColumn={isSupport()}
                                hasDeleteAccess={false}
                            />
                            <Pagination
                                currentPage={currentPage}
                                setCurrentPage={handlePageClick}
                                totalCount={devicesTotalCount}
                                sizePerPage={itemsPerRequest}
                                handleItemsPerPage={handleItemsPerPage}
                            />
                        </>
                    ) : (
                        <MissingData
                            isFilterUsed={filtersUsed}
                            entity={t("deviceManagement")}
                            includeText={!isLoading}
                            clickCallback={() => setShowDevicesModal(true)}
                        />
                    )}
                </div>
            </LoadingOverlay>
            {isSupport() && (
                <>
                    <DevicesModal
                        showDevicesModal={showDevicesModal}
                        setShowDevicesModal={setShowDevicesModal}
                        isModalSubmitting={isDevicesModalSubmitting}
                        data={devicesModalData}
                        setData={setDevicesModalData}
                        isSupport={isSupport}
                        createDeviceAction={createDevice}
                        updateDeviceAction={updateDevice}
                        error={devicesError}
                        isFromSubmitted={isDevicesFromSubmitted}
                    />
                    <ManageFirmwareModal
                        showModal={showFirmwareModal}
                        setShowModal={setShowFirmwareModal}
                        isModalSubmitting={isFirmwareModalSubmitting}
                        data={firmwareModalData}
                        setData={setFirmwareModalData}
                        error={firmwareError}
                        isFromSubmitted={isFirmwareFromSubmitted}
                        submitFirmware={submitFirmware}
                    />
                </>
            )}
        </>
    );
};

export default DeviceManagement;
